import { Injectable } from '@angular/core';
import { UserProfile } from '@app/core/models';
import { DrupalConstants } from '@makiwin/ngx-drupal8-rest';
import moment from 'moment';

/**
 * Service to handle the roles of the current user,
 * and some other utilities related to roles.
 */
@Injectable({
  providedIn: 'root',
})
export class RolesService {
  roleName?: string;
  /**
   * Define if the current user is a kiter.
   */
  isKiter = false;
  /**
   * Define if the current user is an instructor.
   */
  isInstructor = false;
  /**
   * Define if the current user is an assistant.
   */
  isAssistant = false;
  /**
   * Define if the current user is a professional user.
   */
  isProfessional = false;
  /**
   * Define if the current user is a school.
   */
  isSchool = false;
  /**
   * Define if the current user has a coach qualifications.
   */
  isCoach = false;
  /**
   * Define if the current user can shadow an assistant.
   */
  canShadowAssistant = false;
  /**
   * Define if the current user is an expired member.
   */
  isExpired = false;
  /**
   * Define if the current user is an expired member from more than 2 years.
   */
  isExpiredMore2years = false;
  /**
   * Indicates whether the school is expired.
   */
  isSchoolExpxired = false;
  /**
   * Indicates whether the school is in a pending state.
   */
  isSchoolPending = false;
  /**
   * Indicates whether the school is suspended.
   */
  isSchoolSuspended = false;

  /**
   * Current top qualification of the user.
   */
  currentTopQualification?: string;

  private roles: string[] = [];

  /**
   * Roles for enable shadow of instructors.
   */
  private rolesForShadowing = [
    'instructor_level_2_basic',
    'instructor_level_2_plus',
    'instructor_level_2_premium',
    'instructor_level_3_basic',
    'instructor_level_3_plus',
    'instructor_level_3_premium',
    'coach_basic',
    'coach_plus',
    'coach_premium',
    'coach_candidate',
    'assistant_trainer_candidate',
    'assistant_trainer_basic',
    'assistant_trainer_plus',
    'assistant_trainer_premium',
    'assistant_trainer_coach_basic',
    'assistant_trainer_coach_plus',
    'assistant_trainer_coach_premium',
    'assistant_trainer_coach_trainer_basic',
    'assistant_trainer_coach_trainer_plus',
    'assistant_trainer_coach_trainer_premium',
    'coach_trainer_candidate',
    'coach_trainer_basic',
    'coach_trainer_plus',
    'coach_trainer_premium',
    'examiner',
    'examiner_mfa',
    'examiner_coach_trainer',
    'examiner_coach',
    'examiner_coach_trainer_mfa',
    'examiner_coach_mfa',
    'training_master',
  ];

  /**
   * Roles for instructors.
   */
  private rolesInstructor = [
    'instructor_candidate',
    'instructor_level_1_basic',
    'instructor_level_1_plus',
    'instructor_level_1_premium',
    ...this.rolesForShadowing,
  ];

  /**
   * Roles for assistants.
   */
  private rolesAssistant = [
    'assistant_instructor_candidate',
    'assistant_instructor_basic',
    'assistant_instructor_plus',
    'assistant_instructor_premium',
  ];

  /**
   * Roles for professional user.
   */
  private rolesProfessional = [
    ...this.rolesInstructor,
    ...this.rolesAssistant,
    'member_expired',
    'school_pending',
    'school_expired',
    'assistant_candidate',
    'assistant_basic',
    'assistant_plus',
    'assistant_premium',
    'school_free',
    'school_basic',
    'school_plus',
    'translator',
    'editor',
    'administrator',
    'office',
  ];

  /**
   * Init the roles service for the current user.
   */
  init(userProfile?: UserProfile | null) {
    if (
      !DrupalConstants.Connection ||
      !DrupalConstants.Connection.current_user ||
      !DrupalConstants.Connection.current_user.roles
    ) {
      return;
    }

    this.roles = DrupalConstants.Connection.current_user.roles;
    this.roleName = this.roles.join(', ').replace('authenticated, ', '');
    this.isKiter = this.checkStringForRole('kiter', true);
    this.isExpired = this.checkStringForRole('member_expired', true);
    if (this.isExpired) {
      if (userProfile) {
        const twoYearsFromNow = moment().subtract(2, 'years');
        const valueTocheck = userProfile.subscription_expiry
          ? userProfile.subscription_expiry
          : userProfile.next_renewal
          ? userProfile.next_renewal
          : userProfile.d7_expiry_date
          ? userProfile.d7_expiry_date
          : false;
        if (valueTocheck) {
          const subscriptionExpiry = moment(valueTocheck);
          this.isExpiredMore2years = subscriptionExpiry.isBefore(twoYearsFromNow);
        } else {
          this.isExpiredMore2years = false;
        }
      }
    } else {
      this.isExpiredMore2years = false;
    }
    if (!this.isKiter) {
      this.isInstructor = this.checkDefinedRoles(this.rolesInstructor);
      this.isAssistant = this.checkDefinedRoles(this.rolesAssistant);
      this.isProfessional = this.isInstructor || this.isAssistant || this.checkDefinedRoles(this.rolesProfessional);
      this.isSchool = this.checkIsSchool();
      if (this.isSchool) {
        this.isSchoolExpxired = this.userHasRole('school_expired', true);
        this.isSchoolPending = this.userHasRole('school_pending', true);
        this.isSchoolSuspended = this.userHasRole('school_suspended', true);
      }
      this.isCoach = this.checkIsCoach();
      this.canShadowAssistant = this.checkDefinedRoles(this.rolesForShadowing);
    }
  }

  /**
   * Check if the role is a professional instructor.
   * From Instructor level 1 to Examiner.
   *
   * @param {string} role
   * Role to check.
   * @returns
   * True if a role is professional instructor.
   */
  checkIfRoleIsProInstructor(role: string = ''): boolean {
    return this.checkDefinedRoles([...this.rolesInstructor, ...this.rolesAssistant], [role]);
  }

  /**
   * Check if user has a role.
   *
   * @param roleMachineName role machine name or array of machine names
   * if this param is empty, it will check for anonymous user
   * @param contains if the role contains name or not, default to false
   * @param roles
   * Optional array of roles to check. If it's empty, it will check the current user roles.
   * @returns
   * True if the user has the role.
   */
  userHasRole(roleMachineName?: string | string[], contains = false, roles?: string[]): boolean {
    // for anonymous user
    if (!roleMachineName) {
      return this.roles.length === 0;
    }
    // handle multiple roles
    if (Array.isArray(roleMachineName)) {
      let roleContains = false;
      // eslint-disable-next-line @typescript-eslint/no-shadow
      for (const role of roleMachineName) {
        roleContains = roleContains || this.checkStringForRole(role, contains, roles);
      }
      return roleContains;
    } else {
      // handle single role
      return this.checkStringForRole(roleMachineName, contains, roles);
    }
  }

  /**
   * Check if current user has no roles - only have authenticated role.
   *
   * @returns {boolean}
   */
  userHasNoRoles() {
    return this.roles.length === 1 && this.userHasRole('authenticated');
  }

  /**
   * Check if is the current user has the coach level unlocked.
   *
   * @return {boolean}
   * true if the user has the coach level unlocked
   */
  checkIsCoach(roles?: string[]): boolean {
    return (
      this.userHasRole(['coach', 'assistant_trainer', 'examiner'], true, roles) &&
      !this.userHasRole('coach_candidate', false, roles)
    );
  }

  /**
   * Clear the roles of the current user.
   */
  clear(): void {
    this.roles = [];
    this.roleName = '';
    this.isAssistant = false;
    this.isCoach = false;
    this.isInstructor = false;
    this.isSchool = false;
    this.isKiter = false;
  }

  /**
   * Chek if the roles belongs to a professional user.
   * @param {string[]} roles
   * Array of roles to check.
   * @returns {boolean}
   * True if the roles belongs to a professional user.
   */
  checkIsProfessional(roles: string[]): boolean {
    for (const role of this.rolesProfessional) {
      if (this.checkStringForRole(role, false, roles)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks if the 'member_expired' role is present in the provided roles array.
   *
   * @param roles - An array of role strings to check.
   * @returns A boolean indicating whether the 'member_expired' role is present.
   */
  checkIsExpired(roles: string[]): boolean {
    return this.checkStringForRole('member_expired', true, roles);
  }

  /**
   * Checks if the 'member_expired' role is present in the provided roles array.
   *
   * @param roles - An array of role strings to check.
   * @returns A boolean indicating whether the 'member_expired' role is present.
   */
  checkIsSuspended(roles: string[]): boolean {
    return this.checkStringForRole('member_suspended', true, roles);
  }

  /**
   * Get the color for the card of based of top member qualfication.
   * @param {string} topQualification
   * @returns {string}
   * The string class for the color of the card.
   */
  getMembersQualificationsCardColor(topQualification: string): string {
    if (!topQualification) {
      return 'bg-green-gradient';
    }
    topQualification = topQualification.toLowerCase();
    switch (true) {
      case topQualification.indexOf('assistant trainer') !== -1:
      case topQualification.indexOf('trainer') !== -1:
        return 'bg-orange-gradient';
      case topQualification.indexOf('assistant') !== -1:
      case topQualification.indexOf('instructor') !== -1:
      case topQualification.indexOf('coach') !== -1:
      case topQualification.indexOf('coaches') !== -1:
        return 'blue-bg-gradient-light';
      case topQualification.indexOf('examiner') !== -1:
      case topQualification.indexOf('training master') !== -1:
        return 'brown-bg-gradient-dark';
      default:
        return 'bg-green-gradient';
    }
  }

  getUserDashboardRoute(): string {
    if (this.userHasRole('school', true)) {
      return 'school';
    } else if (this.userHasRole(['kiter', 'instructor', 'examiner', 'coach', 'trainer', 'office'], true)) {
      return 'user';
    } else if (this.userHasRole('brand', true)) {
      return 'brand';
    } else {
      return 'unknown';
    }
  }

  /**
   * Check if user has one of the roles in the array of roles.
   * @param roles
   * Array of roles to check.
   * @returns
   * True if the user has one of the roles in the array of roles.
   */
  checkDefinedRoles(roles: string[], rolesToCheck?: string[]): boolean {
    for (const role of roles) {
      if (this.checkStringForRole(role, false, rolesToCheck)) {
        return true;
      }
    }
    return false;
  }

  /**
   * check if is the current user is as school
   *
   * @return {boolean}
   */
  private checkIsSchool(): boolean {
    return this.checkStringForRole('school', true);
  }

  /**
   * Check if user has a role.
   * @param role
   * Role to check, can be complete or partial role name.
   * @param contains
   * If the role contains the role name or not,
   * true if it contains the string, false if it's the complete role name.
   * @param roles
   * Optional array of roles to check. If it's empty, it will check the current user roles.
   * @returns
   * True if the user has the role.
   */
  private checkStringForRole(roleMachineName: string, contains: boolean, roles?: string[]): boolean {
    if (!roles) {
      roles = this.roles;
    }
    if (contains) {
      for (const role of roles) {
        if (role.indexOf(roleMachineName) !== -1) {
          return true;
        }
      }
    }
    return roles.indexOf(roleMachineName) !== -1;
  }
}
